import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Box,
  Grid,
  Card,
  IconButton,
  Link
} from '@mui/material';
import {
  Map,
  Build,
  Close as CloseIcon,
  Groups,
  AccessTime,
  History,
  PeopleAlt,
  EventNote,
  Assessment,
  SecurityOutlined,
  StorageOutlined,
  CakeOutlined,
  CalendarTodayOutlined,
  AssignmentOutlined,
  HowToRegOutlined,
  SpeedOutlined,
  TimelineOutlined,
  CableOutlined,
  DeleteSweepOutlined,
} from '@mui/icons-material';
import { getAllowedWidgetCatalog, WidgetDefinition } from '../dashboardConfig';

export interface WidgetTemplate extends WidgetDefinition {
  icon: React.ReactNode;
}

const WIDGET_ICONS: Record<string, React.ReactNode> = {
  'quick-actions': <Build />, 'organization-overview': <Groups />, 'attendance-summary': <EventNote />,
  'process-statistics': <Assessment />, 'date-time': <AccessTime />, 'recently-visited': <History />,
  'attendance-live-monitor': <PeopleAlt />, 'attendance-geofences': <Map />,
  'access-control-overview': <SecurityOutlined />, 'integrations-overview': <CableOutlined />,
  'recent-audit-logs': <StorageOutlined />, 'today-birthday': <CakeOutlined />,
  'upcoming-events': <CalendarTodayOutlined />, 'my-pending-tasks': <AssignmentOutlined />,
  'process-approval-queue': <HowToRegOutlined />, 'process-sla-status': <SpeedOutlined />,
  'recent-process-activity': <TimelineOutlined />, 'pending-gdpr-deletions': <DeleteSweepOutlined />,
};

interface WidgetPickerProps {
  open: boolean;
  onClose: () => void;
  onSelectWidget: (template: WidgetTemplate) => void;
  /** Types of widgets already on the dashboard — these are hidden from the list. */
  activeTypes?: string[];
  licensedProducts: readonly string[];
}

const WidgetPicker: React.FC<WidgetPickerProps> = ({
  open,
  onClose,
  onSelectWidget,
  activeTypes = [],
  licensedProducts,
}) => {
  const { t } = useTranslation();
  const available: WidgetTemplate[] = getAllowedWidgetCatalog(licensedProducts)
    .filter((widget) => !activeTypes.includes(widget.type))
    .map((widget) => ({ ...widget, icon: WIDGET_ICONS[widget.type] ?? <Build /> }));

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          pt: '14px',
          pb: '8px',
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Typography 
          variant="subtitle1" 
          sx={{ fontWeight: 600, color: 'text.primary' }}
        >
          {t('common:widgetPicker.availableWidgets')}
        </Typography>
        <IconButton aria-label={t('common:actions.close')}
          size="small" 
          onClick={onClose}
          edge="end"
          sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Widget List */}
      <Box 
        onScroll={handleScroll}
        sx={{ 
          overflow: 'auto',
          flex: 1,
          px: 2.5,
          py: 2,
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { width: '6px', backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': { borderRadius: '3px', backgroundColor: 'rgba(0,0,0,0.1)' },
        }}
      >
        {available.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t('common:widgetPicker.allOnDashboard')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={1}>
            {available.map((template) => (
              <Grid item xs={12} key={template.type}>
                <Card
                  onClick={() => onSelectWidget(template)}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                    borderRadius: 2,
                    boxShadow: 'none',
                    transition: 'border-color 120ms',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
                  }}
                >
                  <Box sx={{ 
                    mr: 1.5, 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    bgcolor: 'action.selected',
                    color: 'primary.main',
                    flexShrink: 0,
                  }}>
                    {template.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                      {t(`dashboard:widgetPicker.catalog.${template.i18nKey}.title`)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }} noWrap>
                      {t(`dashboard:widgetPicker.catalog.${template.i18nKey}.description`)}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {t('common:widgetPicker.wantAnother')}{' '}
          <Link component="button" variant="caption" onClick={() => {
            import('../../../components/support/SupportCaseDialog').then(({ openSupportCase }) => {
              openSupportCase({
                category: 'feature',
                subject: t('dashboard:widgetPicker.requestTitle'),
                description: t('dashboard:widgetPicker.requestPlaceholder'),
                priority: 'normal',
              });
            });
          }} sx={{ textDecoration: 'none', color: 'primary.main', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
            {t('common:widgetPicker.tellUs')}
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default WidgetPicker;
